import { Plane, Users, MapPin, Heart, Award, Star, Mail, Phone, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSidebar from "@/components/CartSidebar";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import heroImage from "@/assets/hero-beach.jpg";

const About = () => {
  const location = useLocation();

  // Scroll to contact section when hash is present
  useEffect(() => {
    const scrollToContact = () => {
      const element = document.getElementById("contact");
      if (element) {
        const headerOffset = 80; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    };

    // Check if hash is in URL (either from location.hash or window.location.hash)
    if (location.hash === "#contact" || window.location.hash === "#contact") {
      // Small delay to ensure page is fully rendered
      setTimeout(scrollToContact, 100);
    }
  }, [location.hash, location.pathname]);
  const stats = [
    { icon: Users, value: "500+", label: "Happy Travelers" },
    { icon: MapPin, value: "50+", label: "Destinations" },
    { icon: Plane, value: "100+", label: "Trips Completed" },
    { icon: Heart, value: "98%", label: "Satisfaction Rate" },
  ];

  const values = [
    {
      icon: Users,
      title: "Connection First",
      description: "We carefully curate groups of 20 compatible singles (10 men, 10 women) who share a love for travel and adventure, creating opportunities for meaningful connections.",
    },
    {
      icon: Heart,
      title: "Organic Moments",
      description: "Our itineraries are packed with fun activities designed to break the ice naturally, creating authentic moments in beautiful places where connections can flourish.",
    },
    {
      icon: Award,
      title: "Verified & Safe",
      description: "Safe Travel Guarantee, Verified Profiles, and Expert Guides ensure your safety and peace of mind throughout your journey.",
    },
    {
      icon: Plane,
      title: "Travel Reveals Character",
      description: "By the end of each trip, you'll have made 19 new friends, and perhaps, found the one who makes your heart race. Travel reveals character.",
    },
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Mumbai, India",
      rating: 5,
      text: "Slate us changed my perspective on solo travel. I made lifelong friends and explored Manali in a way I never could alone. The group dynamics were perfect!",
      trip: "Manali Mountain Escape",
    },
    {
      name: "Rahul Mehta",
      location: "Delhi, India",
      rating: 5,
      text: "As someone who was hesitant about solo travel, Slate us made it so easy and safe. The Goa trip exceeded all my expectations. Highly recommended!",
      trip: "Goa Beach Vibes",
    },
    {
      name: "Ananya Patel",
      location: "Bangalore, India",
      rating: 5,
      text: "The best part about Slate us is the community. Everyone is so welcoming, and the trips are perfectly organized. Can't wait for my next adventure!",
      trip: "Manali Mountain Escape",
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <CartSidebar />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
          <div className="absolute inset-0">
            <img 
              src={heroImage} 
              alt="Travelers exploring together" 
              className="w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground/70 via-foreground/50 to-foreground/70" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4 text-center">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/20 text-primary-foreground text-sm font-medium mb-6 backdrop-blur-sm animate-fade-in">
              Our Story
            </span>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-primary-foreground mb-6 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              Traveling Together is Magical
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "0.2s" }}>
              Traveling alone is empowering, but traveling together is magical. Whether you find a soulmate, 
              a travel partner, or a lifelong friend, you won't return home the same.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 md:py-28 bg-gradient-sunset">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Our Mission
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-6">
                Connection Through Travel
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Slate us bridges the gap between a solo adventure and a matchmaking service. 
                We carefully curate groups of 20 compatible singles (10 men, 10 women) who share 
                a love for travel and adventure. Our itineraries are packed with fun activities 
                designed to break the ice naturally, creating organic moments in beautiful places 
                where connections can flourish.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-0 shadow-card">
                <CardContent className="p-8">
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                    Our Vision
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    To become the leading platform for singles seeking connection through travel, 
                    creating a global community where every adventure is shared, every destination 
                    is explored, and every journey creates lasting relationships—whether romantic, 
                    platonic, or lifelong friendships.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-card">
                <CardContent className="p-8">
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                    Our Promise
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We promise authentic experiences, genuine connections, and memories that last 
                    a lifetime. Every trip is carefully planned with verified profiles, expert guides, 
                    and a safe travel guarantee. Travel reveals character, and by the end of each journey, 
                    you'll have made 19 new friends, and perhaps, found the one who makes your heart race.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <Card key={index} className="border-0 shadow-card text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <CardContent className="p-8">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <div className="text-4xl font-bold text-foreground mb-2">{stat.value}</div>
                      <div className="text-sm text-muted-foreground">{stat.label}</div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 md:py-28 bg-gradient-sunset">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Why Choose Us
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
                What Makes Us Different
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We're not just a travel company—we're a community of adventurers who believe in the power of shared experiences.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card key={index} className="border-0 shadow-card hover:shadow-elevated transition-all duration-300 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                        {value.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Testimonials
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
                Stories from Our Travelers
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Don't just take our word for it—hear from adventurers who've journeyed with us.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-card animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                      ))}
                    </div>
                    <div className="text-4xl text-primary/30 mb-4 leading-none">"</div>
                    <p className="text-muted-foreground mb-6 leading-relaxed italic">
                      {testimonial.text}
                    </p>
                    <div className="border-t border-border pt-4">
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                      <p className="text-xs text-primary mt-2">{testimonial.trip}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Us Section */}
        <section id="contact" className="py-20 md:py-28 bg-gradient-sunset">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                Get in Touch
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
                Contact Us
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Have questions? We'd love to hear from you. Reach out to us and we'll respond as soon as possible.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="border-0 shadow-elevated">
                <CardContent className="p-8 md:p-12">
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-2xl font-semibold text-foreground">Slate us</h3>
                        <p className="text-sm text-muted-foreground">Company Name</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/50">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Registered Address</h4>
                        <p className="text-sm text-muted-foreground">South Delhi</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/50">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Contact Number</h4>
                        <a 
                          href="tel:+919024286490" 
                          className="text-sm text-primary hover:underline"
                        >
                          +91 9024286490
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 rounded-lg bg-muted/50 md:col-span-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">Email Address</h4>
                        <a 
                          href="mailto:support@slateus.com" 
                          className="text-sm text-primary hover:underline"
                        >
                          support@slateus.com
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 md:py-28 bg-gradient-sunset">
          <div className="container mx-auto px-4">
            <Card className="border-0 shadow-elevated max-w-4xl mx-auto bg-card">
              <CardContent className="p-12 text-center">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Ready to Start Your Adventure?
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Join our community of solo travelers and discover the world with new friends. 
                  Your next adventure is just a click away.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/#trips">Explore Trips</Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/auth">Create Account</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;

